import { useState, useEffect } from "react";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import "./Navigation.css";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  const PHONE_NUMBER = "+351 915 129 868";
  const PHONE_LINK = "tel:+351915129868";

  const EMAIL = "book.tripalgarve@gmail.com";

  const WHATSAPP_NUMBER = "351915129868";

  const WHATSAPP_MESSAGE = encodeURIComponent(
    "Olá, gostaria de pedir informações sobre um transfer."
  );

  const WHATSAPP_LINK = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${WHATSAPP_MESSAGE}`;

    const menuItems = [
    { id: "home", label: t("navigation.home") },
    { id: "prices", label: t("navigation.prices") },
    { id: "about", label: t("navigation.about") },
    { id: "contacts", label: t("navigation.contacts") },
    { id: "help", label: t("navigation.help") },
  ];

  const currentPath = location.pathname;
  const isHomePage = currentPath === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (id: string) => {
    const path = id === "home" ? "/" : `/${id}`;
    return currentPath === path;
  };

  const handleNavigation = (id: string) => {
    const path = id === "home" ? "/" : `/${id}`;

    navigate(path);
    setIsMenuOpen(false);
  };

  const navClassName = `navigation ${
    !isHomePage || isScrolled ? "navigation-solid" : ""
  }`;

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={navClassName}
      >
        <div className="navigation-container">
          <div className="navigation-inner">
            {/* LOGO */}
            <div
              className="logo"
              onClick={() => handleNavigation("home")}
            >
              <div className="logo-icon">
                <img
                  src="/logo.png"
                  alt="Francesco Airport Transfers Algarve Logo"
                  className="logo-image"
                />
              </div>

              <div className="logo-text">
                <span className="title">Francesco</span>

                <div className="subtitle">
                  Airport Transfers Algarve
                </div>
              </div>
            </div>

            {/* DESKTOP MENU */}
            <div className="nav-menu">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  className={`nav-link ${
                    isActive(item.id) ? "active" : ""
                  }`}
                  onClick={() => handleNavigation(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* DESKTOP CTA */}
            <div className="nav-cta">
              <div className="separator"></div>

              <a href={PHONE_LINK} className="cta-phone">
                <Phone className="icon" />
                <span>{PHONE_NUMBER}</span>
              </a>

              <button
                className="cta-button"
                onClick={() => navigate("/booking")}
              >
  {t("navigation.booking")}
              </button>
              <LanguageSwitcher />

            </div>

            {/* MOBILE MENU BUTTON */}
            <button
              className="mobile-button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Abrir menu"
            >
              {isMenuOpen ? (
                <X className="icon" />
              ) : (
                <Menu className="icon" />
              )}
            </button>
          </div>

          {/* MOBILE MENU */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mobile-menu"
              >
                <div className="mobile-menu-inner">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      className={`mobile-link ${
                        isActive(item.id) ? "active" : ""
                      }`}
                      onClick={() => handleNavigation(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}

                  <a href={PHONE_LINK} className="mobile-phone">
                    <Phone className="icon" />
                    <span>{PHONE_NUMBER}</span>
                  </a>

                  <a
                    href={`mailto:${EMAIL}`}
                    className="mobile-phone"
                  >
                    <span>{EMAIL}</span>
                  </a>

                  <button
                    className="mobile-cta-button"
                    onClick={() => {
                      navigate("/booking");
                      setIsMenuOpen(false);
                    }}
                  >
  {t("navigation.booking")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>

      {/* WHATSAPP FLOATING BUTTON */}
      <a
        href={WHATSAPP_LINK}
        className="whatsapp-floating-button"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactar pelo WhatsApp"
      >
        <MessageCircle className="whatsapp-icon" />
      </a>
    </>
  );
}